// =============================================================================
// SIREPPAR - Bandeja de Expedientes de Candidatura (Card D)
// =============================================================================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { candidaturaExpedienteService, convocatoriaService, usuarioService } from '../../services/api';

const ESTADO_BADGE: Record<string, string> = {
  PRESENTADO: 'bg-blue-100 text-blue-800',
  EN_ANALISIS: 'bg-amber-100 text-amber-800',
  OBSERVADO: 'bg-red-100 text-red-800',
  EN_SECRETARIO: 'bg-purple-100 text-purple-800',
  EN_TEP: 'bg-indigo-100 text-indigo-800',
  VIGENTE: 'bg-green-100 text-green-800',
  RECHAZADO: 'bg-red-100 text-red-800',
};

const TIPO_BADGE: Record<string, string> = {
  MUNICIPAL: 'bg-blue-100 text-blue-800',
  DISTRITO: 'bg-gray-100 text-gray-700',
  PROVINCIAL: 'bg-green-100 text-green-800',
};

type TabFiltro = 'PRESENTADO' | 'EN_ANALISIS' | 'OBSERVADO' | 'EN_TEP' | 'VIGENTE' | 'TODOS';

function formatFecha(fecha?: string | null): string {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
}

export default function BandejaCandidaturasPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const puedeAsignar = ['SUPERVISOR', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');

  const [tab, setTab] = useState<TabFiltro>('TODOS');
  const [filtroAnalista, setFiltroAnalista] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [modalAsignar, setModalAsignar] = useState<{ expedienteId: string } | null>(null);
  const [analistaSeleccionado, setAnalistaSeleccionado] = useState('');

  const { data: convocatoria } = useQuery({
    queryKey: ['convocatoria-actual'],
    queryFn: async () => (await convocatoriaService.getActual()).data.data,
  });

  const { data: contadores } = useQuery({
    queryKey: ['candidaturas-contadores'],
    queryFn: async () => (await candidaturaExpedienteService.contadores()).data.data,
  });

  const { data: analistas } = useQuery({
    queryKey: ['usuarios-analistas'],
    queryFn: async () => (await usuarioService.list({ rol: 'ANALISTA,SUPERVISOR', estado: 'ACTIVO' })).data.data,
    enabled: puedeAsignar,
  });

  const estadoParaApi = tab === 'TODOS' ? undefined : tab;

  const { data: expedientes, isLoading } = useQuery({
    queryKey: ['candidaturas-bandeja', estadoParaApi, filtroAnalista, busqueda],
    queryFn: async () => (await candidaturaExpedienteService.listar({
      ...(estadoParaApi && { estado: estadoParaApi }),
      ...(filtroAnalista && { analistaId: filtroAnalista }),
      ...(busqueda && { busqueda }),
    })).data.data,
  });

  const asignar = async () => {
    if (!modalAsignar || !analistaSeleccionado) return;
    try {
      await candidaturaExpedienteService.asignar(modalAsignar.expedienteId, analistaSeleccionado);
      toast.success('Analista asignado');
      setModalAsignar(null);
      setAnalistaSeleccionado('');
      queryClient.invalidateQueries({ queryKey: ['candidaturas-bandeja'] });
      queryClient.invalidateQueries({ queryKey: ['candidaturas-contadores'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo asignar');
    }
  };

  const diasRestantes = convocatoria?.plazos?.cierre_candidaturas
    ? Math.ceil((new Date(convocatoria.plazos.cierre_candidaturas).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24))
    : null;

    const totalTodos = contadores
    ? (contadores.presentados ?? 0) + (contadores.enAnalisis ?? 0) + (contadores.observados ?? 0) + (contadores.enTep ?? 0) + (contadores.oficializados ?? 0)
    : undefined;

  const TABS: Array<{ key: TabFiltro; label: string; count: number | undefined }> = [
    { key: 'PRESENTADO', label: 'Presentados', count: contadores?.presentados },
    { key: 'EN_ANALISIS', label: 'En análisis', count: contadores?.enAnalisis },
    { key: 'OBSERVADO', label: 'Observados', count: contadores?.observados },
    { key: 'EN_TEP', label: 'En el TEP', count: contadores?.enTep },
    { key: 'VIGENTE', label: 'Finalizados', count: contadores?.oficializados },
    { key: 'TODOS', label: 'Todos', count: totalTodos },
  ];


  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Gestión de Expedientes — Candidaturas Gaiman</h1>
        <p className="text-sm text-gray-500">
          Flujo: Presentado → Análisis → Resolución
          {convocatoria?.denominacion && <> · {convocatoria.denominacion}</>}
          {diasRestantes !== null && <> · Cierre de presentación: {diasRestantes >= 0 ? `${diasRestantes} días` : 'plazo vencido'}</>}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Presentados</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{contadores?.presentados ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-amber-500">
          <div className="text-sm text-gray-500">Análisis</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{contadores?.enAnalisis ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Observados</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{contadores?.observados ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-indigo-500">
          <div className="text-sm text-gray-500">En el TEP</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{contadores?.enTep ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
        <div className="text-sm text-gray-500">Finalizados</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{contadores?.oficializados ?? '—'}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow">
        <div className="border-b border-gray-100 flex gap-1 px-2 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label} {t.count !== undefined && <span className="text-gray-400">({t.count})</span>}
            </button>
          ))}
        </div>

        <div className="p-4 flex flex-wrap gap-3 items-end border-b border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Analista</label>
            <select value={filtroAnalista} onChange={e => setFiltroAnalista(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos</option>
              <option value="SIN_ASIGNAR">Sin asignar</option>
              {analistas?.map((a: any) => (
                <option key={a.id} value={a.id}>{a.nombre} {a.apellido}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
            <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Número, agrupación o candidato"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Expediente</th>
                <th className="text-left px-4 py-3">Agrupación</th>
                <th className="text-left px-4 py-3">Candidato</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Analista</th>
                <th className="text-left px-4 py-3">Alertas</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">Cargando...</td></tr>
              )}
              {!isLoading && expedientes?.data?.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">No hay expedientes con estos filtros</td></tr>
              )}
              {expedientes?.data?.map((e: any) => (
                <tr key={e.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/candidaturas/gaiman/expedientes/${e.id}`)}>
                  <td className="px-4 py-3">
                  <Link
                      to={`/candidaturas/gaiman/expedientes/${e.id}`}
                      onClick={(ev) => ev.stopPropagation()}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {e.numero}
                    </Link>
                    <div className="text-xs text-gray-400">Candidatura · {formatFecha(e.fechaPresentacion)}</div>
                  </td>
                  <td className="px-4 py-3">
                    {e.partido?.denominacion}
                    <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${TIPO_BADGE[e.partido?.tipo] || 'bg-gray-100 text-gray-700'}`}>
                      {e.partido?.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3">{e.candidato || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_BADGE[e.estado] || 'bg-gray-100 text-gray-700'}`}>
                      {e.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">{e.asignado ? `${e.asignado.nombre} ${e.asignado.apellido}` : <span className="text-gray-400 italic">Sin asignar</span>}</td>
                  <td className="px-4 py-3">
                    {e.observacionesActivas > 0 ? (
                      <span className="text-red-600 font-medium">{e.observacionesActivas} observación{e.observacionesActivas === 1 ? '' : 'es'}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {puedeAsignar && (
                      <button
                      onClick={(ev) => { ev.stopPropagation(); setModalAsignar({ expedienteId: e.id }); }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline mr-3"
                      >
                        Asignar
                      </button>
                    )}
                    <Link
                      to={`/candidaturas/gaiman/expedientes/${e.id}`}
                      onClick={(ev) => ev.stopPropagation()}
                      className="inline-block px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
                    >
                      Gestionar
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalAsignar && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Asignar analista</h3>
            <select value={analistaSeleccionado} onChange={e => setAnalistaSeleccionado(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4">
              <option value="">Seleccioná un analista...</option>
              {analistas?.map((a: any) => (
                <option key={a.id} value={a.id}>{a.nombre} {a.apellido}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setModalAsignar(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={asignar} disabled={!analistaSeleccionado} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm">Asignar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
